// Unified input: keyboard, mouse drag camera, touch joystick + buttons, gamepad.
import { clamp } from './util.js';
import { TouchLayout } from './touch-layout.js';
import { haptics } from './haptics.js';

export class Input {
  constructor(canvas, touchRoot) {
    this.canvas = canvas;
    this.keys = new Set();
    this.pressed = new Set();
    this.moveX = 0;
    this.moveY = 0;
    this.camDX = 0;
    this.camDY = 0;
    this.wheel = 0;
    this.taps = [];
    this.jumpHeld = false;
    this.enabled = true;
    this.touchMode = false;
    this.joy = { id: null, ox: 0, oy: 0, x: 0, y: 0 };
    this.cam = { id: null, lx: 0, ly: 0, sx: 0, sy: 0, moved: 0, t: 0 };
    this.cameraTouches = new Map();
    this.pinchDistance = 0;
    this.mouse = { down: false, lx: 0, ly: 0, sx: 0, sy: 0, moved: 0, button: 0 };
    this.btnJump = false;
    this.gpPrev = [];
    this.buildTouch(touchRoot);
    this.layout = new TouchLayout(this);
    this.layout.apply();

    window.addEventListener('keydown', (e) => {
      if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) return;
      const k = e.key.toLowerCase();
      if (this.layout.editing) {
        if (k === 'escape') this.onLayoutCancel?.();
        return;
      }
      if ([' ', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'].includes(k)) e.preventDefault();
      if (!this.keys.has(k)) this.pressed.add(k);
      this.keys.add(k);
      if (this.touchMode && !['shift', 'control', 'alt', 'meta'].includes(k)) this.setTouchMode(false);
    });
    window.addEventListener('keyup', (e) => this.keys.delete(e.key.toLowerCase()));
    window.addEventListener('blur', () => {
      this.keys.clear();
      this.joy.id = null;
      this.cam.id = null;
      this.cameraTouches.clear();
      this.pinchDistance = 0;
      this.mouse.down = false;
      this.btnJump = false;
      this.updateJoyVisual();
    });

    canvas.addEventListener('pointerdown', (e) => this.onDown(e));
    window.addEventListener('pointermove', (e) => this.onMove(e));
    window.addEventListener('pointerup', (e) => this.onUp(e));
    window.addEventListener('pointercancel', (e) => this.onUp(e, true));
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    canvas.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        this.wheel += e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? window.innerHeight : 1);
      },
      { passive: false }
    );
    document.addEventListener('gesturestart', (e) => e.preventDefault());
  }

  buildTouch(root) {
    this.touchEl = document.createElement('div');
    this.touchEl.id = 'touch';
    this.touchEl.innerHTML = `
      <div class="joy-base" id="joy-base"><div class="joy-knob" id="joy-knob"></div></div>
      <button class="tbtn tbtn-action" id="tbtn-action" aria-label="Action"><span class="ticon">⚔️</span><span class="tlabel" id="tbtn-action-label">HIT</span></button>
      <button class="tbtn tbtn-jump" id="tbtn-jump" aria-label="Jump"><span class="ticon">⤒</span><span class="tlabel">JUMP</span></button>
    `;
    root.appendChild(this.touchEl);
    const safeArea = document.createElement('div');
    safeArea.id = 'touch-safe-area';
    safeArea.setAttribute('aria-hidden', 'true');
    root.appendChild(safeArea);
    this.joyBase = this.touchEl.querySelector('#joy-base');
    this.joyKnob = this.touchEl.querySelector('#joy-knob');
    this.joyBase.addEventListener('pointerdown', e => {
      if (this.layout?.editing || !this.touchControlsActive) return;
      e.preventDefault(); e.stopPropagation();
      this.setTouchMode(true);
      this.startJoy(e);
      this.joyBase.setPointerCapture(e.pointerId);
    });
    const bind = (id, down, up) => {
      const el = this.touchEl.querySelector(id);
      el.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        el.classList.add('down');
        down();
        try {
          el.setPointerCapture(e.pointerId);
        } catch (_) {
          /* ignore */
        }
      });
      const end = (e) => {
        el.classList.remove('down');
        up && up();
      };
      el.addEventListener('pointerup', end);
      el.addEventListener('pointercancel', end);
      el.addEventListener('lostpointercapture', end);
    };
    bind('#tbtn-jump', () => {
      this.pressed.add('jump');
      this.btnJump = true;
    }, () => (this.btnJump = false));
    bind('#tbtn-action', () => {this.pressed.add('action');this.btnAction=true;},()=>this.btnAction=false);
  }

  setTouchMode(on) {
    if (this.touchMode === on) return;
    this.touchMode = on;
    document.body.classList.toggle('touch-mode', on);
    this.layout?.apply();
    this.onTouchModeChange?.();
  }

  setActionLabel(text, icon = '⚔️') {
    const l = this.touchEl.querySelector('#tbtn-action-label');
    const i = this.touchEl.querySelector('#tbtn-action .ticon');
    if (l.textContent !== text) l.textContent = text;
    if (i.textContent !== icon) i.textContent = icon;
    const button=this.touchEl.querySelector('#tbtn-action');
    button.classList.toggle('glow', ['LEARN','SOLVE','PLACE'].includes(text));
    button.setAttribute('aria-label',text[0]+text.slice(1).toLowerCase());
  }

  onDown(e) {
    if (this.layout.editing) return;
    if (e.pointerType === 'touch') {
      this.setTouchMode(true);
      haptics.unlock();
      e.preventDefault();
      const w = window.innerWidth;
      if (!this.layout.hasCustom('move') && e.clientX < w * 0.42 && e.clientY > window.innerHeight * 0.5 && this.joy.id === null && this.touchControlsActive && this.cameraTouches.size === 0) {
        this.startJoy(e);
      } else if (this.cameraTouches.size < 2) {
        this.cameraTouches.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (this.cameraTouches.size === 2) {
          const [a, b] = [...this.cameraTouches.values()];
          this.camDX = this.camDY = 0;
          this.pinchDistance = Math.hypot(a.x - b.x, a.y - b.y);
          this.cam.moved = 999;
          return;
        }
        this.cam.id = e.pointerId;
        this.cam.lx = this.cam.sx = e.clientX;
        this.cam.ly = this.cam.sy = e.clientY;
        this.cam.moved = 0;
        this.cam.t = performance.now();
      }
      return;
    }
    if (e.pointerType === 'mouse' && this.touchMode) this.setTouchMode(false);
    this.mouse.down = true;
    this.mouse.button = e.button;
    this.mouse.lx = this.mouse.sx = e.clientX;
    this.mouse.ly = this.mouse.sy = e.clientY;
    this.mouse.moved = 0;
    try {
      this.canvas.setPointerCapture(e.pointerId);
    } catch (_) {
      /* ignore */
    }
  }

  onMove(e) {
    if (this.layout.editing) return;
    if (e.pointerType === 'touch') {
      if (e.pointerId === this.joy.id) {
        const R = 55;
        let dx = e.clientX - this.joy.ox, dy = e.clientY - this.joy.oy;
        const d = Math.hypot(dx, dy);
        if (d > R) {
          // drag the base along so the stick never feels "stuck"
          this.joy.ox += (dx / d) * (d - R);
          this.joy.oy += (dy / d) * (d - R);
          dx = e.clientX - this.joy.ox;
          dy = e.clientY - this.joy.oy;
        }
        this.joy.x = dx / R;
        this.joy.y = dy / R;
        this.updateJoyVisual();
      } else if (this.cameraTouches.has(e.pointerId)) {
        this.cameraTouches.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (this.cameraTouches.size === 2) {
          const [a, b] = [...this.cameraTouches.values()];
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          this.wheel += (this.pinchDistance - d) * 4;
          this.pinchDistance = d;
          this.cam.moved = 999;
          return;
        }
        this.camDX += (e.clientX - this.cam.lx) * 1.25;
        this.camDY += (e.clientY - this.cam.ly) * 1.1;
        this.cam.moved += Math.abs(e.clientX - this.cam.lx) + Math.abs(e.clientY - this.cam.ly);
        this.cam.lx = e.clientX;
        this.cam.ly = e.clientY;
      }
      return;
    }
    if (!this.mouse.down) return;
    const dx = e.clientX - this.mouse.lx, dy = e.clientY - this.mouse.ly;
    this.mouse.moved += Math.abs(dx) + Math.abs(dy);
    if (this.mouse.moved > 4) {
      this.camDX += dx;
      this.camDY += dy;
    }
    this.mouse.lx = e.clientX;
    this.mouse.ly = e.clientY;
  }

  onUp(e, cancel = false) {
    if (e.pointerType === 'touch') {
      if (e.pointerId === this.joy.id) {
        this.joy.id = null;
        this.joy.x = this.joy.y = 0;
        this.updateJoyVisual();
      } else if (this.cameraTouches.has(e.pointerId)) {
        const wasPinching = this.cameraTouches.size === 2;
        this.cameraTouches.delete(e.pointerId);
        this.pinchDistance = 0;
        if (this.cameraTouches.size) {
          const [id, pt] = [...this.cameraTouches.entries()][0];
          this.cam.id = id;
          this.cam.lx = pt.x; this.cam.ly = pt.y;
          this.cam.moved = 999;
          return;
        }
        if (!wasPinching && !cancel && this.cam.moved < 12 && performance.now() - this.cam.t < 350) this.taps.push({ x: e.clientX, y: e.clientY, touch: true });
        this.cam.id = null;
      }
      return;
    }
    if (!this.mouse.down) return;
    this.mouse.down = false;
    if (!cancel && this.mouse.moved <= 4) {
      this.taps.push({ x: e.clientX, y: e.clientY, button: this.mouse.button });
      if (this.mouse.button === 0) this.pressed.add('click');
    }
  }

  updateJoyVisual() {
    if (!this.joyBase) return;
    if (this.joy.id === null) {
      this.joyBase.classList.remove('active');
      this.joyBase.style.removeProperty('left'); this.joyBase.style.removeProperty('top');
      this.joyKnob.style.transform = 'translate(-50%, -50%)';
      this.layout?.applyControl('move');
      return;
    }
    this.joyBase.classList.add('active');
    this.joyBase.style.left = this.joy.ox + 'px';
    this.joyBase.style.top = this.joy.oy + 'px';
    this.joyKnob.style.transform = `translate(calc(-50% + ${this.joy.x * 55}px), calc(-50% + ${this.joy.y * 55}px))`;
  }

  startJoy(e) {
    if (this.joy.id !== null || this.cameraTouches.size) return;
    this.joy.id = e.pointerId;
    this.joy.ox = e.clientX; this.joy.oy = e.clientY;
    this.joy.x = this.joy.y = 0;
    this.updateJoyVisual();
  }

  // Called once per frame before game logic
  update() {
    let x = 0, y = 0;
    const k = this.keys;
    if (k.has('w') || k.has('arrowup')) y += 1;
    if (k.has('s') || k.has('arrowdown')) y -= 1;
    if (k.has('a') || k.has('arrowleft')) x -= 1;
    if (k.has('d') || k.has('arrowright')) x += 1;
    if (this.joy.id !== null) {
      const jx = this.joy.x, jy = -this.joy.y;
      const m = Math.hypot(jx, jy);
      if (m > 0.12) {
        const s = Math.min(1, (m - 0.12) / 0.75) / m;
        x += jx * s;
        y += jy * s;
      }
    }
    this.jumpHeld = k.has(' ') || this.btnJump;
    // gamepad
    const pads = navigator.getGamepads ? navigator.getGamepads() : [];
    for (const gp of pads) {
      if (!gp || !gp.connected) continue;
      const dz = (v) => (Math.abs(v) < 0.18 ? 0 : v);
      x += dz(gp.axes[0] || 0);
      y -= dz(gp.axes[1] || 0);
      this.camDX += dz(gp.axes[2] || 0) * 14;
      this.camDY += dz(gp.axes[3] || 0) * 10;
      const b = gp.buttons.map((bb) => bb.pressed);
      const edge = (i) => b[i] && !this.gpPrev[i];
      if (edge(0)) this.pressed.add('jump');
      if (edge(2) || edge(1)) this.pressed.add('action');
      if (edge(9)) this.pressed.add('escape');
      if (edge(12)) this.pressed.add('arrowup');
      if (edge(13)) this.pressed.add('arrowdown');
      if (edge(14)) this.pressed.add('arrowleft');
      if (edge(15)) this.pressed.add('arrowright');
      if (b[0]) this.jumpHeld = true;
      this.gpPrev = b;
      break;
    }
    const m = Math.hypot(x, y);
    if (m > 1) {
      x /= m;
      y /= m;
    }
    this.moveX = x;
    this.moveY = y;
  }

  get jumpPressed() {
    return this.pressed.has(' ') || this.pressed.has('jump');
  }
  get actionHeld() { return this.btnAction || this.keys.has('e') || this.keys.has('f'); }
  get actionPressed() {
    return this.pressed.has('action') || this.pressed.has('e') || this.pressed.has('f') || this.pressed.has('j') || this.pressed.has('enter') || this.pressed.has('click');
  }
  get pausePressed() {
    return this.pressed.has('escape') || this.pressed.has('p');
  }
  wasPressed(k) {
    return this.pressed.has(k);
  }

  endFrame() {
    this.pressed.clear();
    this.camDX = 0;
    this.camDY = 0;
    this.wheel = 0;
    this.taps.length = 0;
  }

  reset() {
    this.keys.clear();
    this.pressed.clear();
    this.cam.id = null;
    this.cameraTouches.clear();
    this.pinchDistance = 0;
    this.camDX = this.camDY = this.wheel = 0;
    this.mouse.down = false;
    this.taps.length = 0;
    this.joy.id = null;
    this.joy.x = this.joy.y = 0;
    this.btnJump = false;
    this.btnAction = false;
    this.updateJoyVisual();
  }

  showTouchControls(on) {
    this.touchControlsActive = on;
    this.touchEl.classList.toggle('active', on);
    if (!on) {
      this.joy.id = null;
      this.joy.x = this.joy.y = 0;
      this.updateJoyVisual();
    }
  }
}

export const clampMove = (v) => clamp(v, -1, 1);
